/**
 * ExplainToggle.jsx - GRANULLO Orientation Layer
 * 
 * GRANULLO_COMMITMENT_INFRASTRUCTURE_v1.1
 * 
 * Lightweight, global "Explain" toggle that provides:
 * - Copy-only explanations
 * - No inference
 * - No AI language
 * - No logic coupling
 * - Contextual "what you're seeing" orientation
 * 
 * This is NOT Clarity Mode revival.
 * This is orientation and ease only.
 * 
 * IMPLEMENTATION:
 * 1. Single bulb icon in header
 * 2. Toggle adds data-explain="true" to document
 * 3. CSS shows/hides .explain-content spans
 * 4. NO logic changes, NO API calls
 * 5. localStorage persistence only
 */

import React, { useState, useEffect, createContext, useContext } from 'react';
import { Lightbulb, LightbulbOff } from 'lucide-react';
import { cn } from '../../lib/utils';

// =============================================================================
// CONTEXT
// =============================================================================

const ExplainContext = createContext({
  isExplainEnabled: false,
  toggleExplain: () => {},
});

export const useExplain = () => useContext(ExplainContext);

// =============================================================================
// PROVIDER
// =============================================================================

const STORAGE_KEY = 'granullo-explain-enabled';

export const ExplainProvider = ({ children }) => {
  const [isExplainEnabled, setIsExplainEnabled] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(STORAGE_KEY) === 'true';
  });
  
  // Sync to document attribute
  useEffect(() => {
    if (isExplainEnabled) {
      document.documentElement.setAttribute('data-explain', 'true');
    } else {
      document.documentElement.removeAttribute('data-explain');
    }
  }, [isExplainEnabled]);
  
  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(isExplainEnabled));
  }, [isExplainEnabled]);
  
  const toggleExplain = () => {
    setIsExplainEnabled(prev => !prev);
  };
  
  return (
    <ExplainContext.Provider value={{ isExplainEnabled, toggleExplain }}>
      {children}
    </ExplainContext.Provider>
  );
};

// =============================================================================
// TOGGLE BUTTON (For header)
// =============================================================================

export const ExplainToggle = ({ className }) => {
  const { isExplainEnabled, toggleExplain } = useExplain();
  
  return (
    <button
      onClick={toggleExplain}
      className={cn(
        "p-2 rounded-lg transition-all",
        isExplainEnabled 
          ? "bg-amber-500/10 text-amber-600 hover:bg-amber-500/20" 
          : "text-muted-foreground hover:text-foreground hover:bg-muted/50",
        className
      )}
      title={isExplainEnabled ? "Hide explanations" : "Show explanations"}
      aria-label={isExplainEnabled ? "Hide explanations" : "Show explanations"}
      aria-pressed={isExplainEnabled}
      data-testid="explain-toggle"
    >
      {isExplainEnabled ? (
        <Lightbulb className="w-4 h-4" />
      ) : (
        <LightbulbOff className="w-4 h-4" />
      )}
    </button>
  );
};

// =============================================================================
// INLINE EXPLANATION COMPONENT
// =============================================================================

/**
 * ExplainContent - Inline explanation that shows when Explain mode is enabled
 * 
 * Usage:
 * <p>
 *   This is the Actuary view.
 *   <ExplainContent>
 *     The Actuary shows contextual exposure of commitments in dollars.
 *   </ExplainContent>
 * </p>
 */
export const ExplainContent = ({ children, className }) => {
  const { isExplainEnabled } = useExplain();
  
  if (!isExplainEnabled) return null;
  
  return (
    <span 
      className={cn(
        "inline-block ml-1 px-2 py-0.5 rounded text-[11px] bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/20",
        className
      )}
      data-explain-content
    >
      {children}
    </span>
  );
};

/**
 * ExplainBlock - Block-level explanation that shows when Explain mode is enabled
 */
export const ExplainBlock = ({ children, className }) => {
  const { isExplainEnabled } = useExplain();
  
  if (!isExplainEnabled) return null;
  
  return (
    <div 
      className={cn(
        "mt-2 p-3 rounded-lg text-[12px] bg-amber-500/5 text-amber-700 dark:text-amber-300 border border-amber-500/20",
        className
      )}
      data-explain-block
    >
      <div className="flex items-start gap-2">
        <Lightbulb className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <div>{children}</div>
      </div>
    </div>
  );
};

// =============================================================================
// CSS INJECTION (Global styles for explain mode)
// =============================================================================

/**
 * ExplainStyles - Inject global CSS for explain mode
 * Include this once at the app root level
 */
export const ExplainStyles = () => (
  <style>{`
    /* Explain mode indicator */
    [data-explain="true"] .explain-indicator {
      display: flex;
    }
    
    /* Show explain content when enabled */
    [data-explain="true"] [data-explain-content],
    [data-explain="true"] [data-explain-block] {
      display: inline-block;
    }
    
    /* Subtle highlight on explainable elements */
    [data-explain="true"] [data-explainable] {
      outline: 1px dashed var(--g-badge-warning-text);
      outline-offset: 2px;
    }
  `}</style>
);

// =============================================================================
// EXPORTS
// =============================================================================

export default {
  ExplainProvider,
  ExplainToggle,
  ExplainContent,
  ExplainBlock,
  ExplainStyles,
  useExplain,
};
